import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { RecipientResponse } from "@/components/RecipientResponse";
import { MapPin, Wrench } from "lucide-react";

const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "");
const SEV = {
  low: "bg-[#E0F2FE] text-[#075985] border-[#BAE6FD]",
  moderate: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  urgent: "bg-[#FEE2E2] text-[#B91C1C] border-[#FECACA]",
};

function Img({ src, alt }) {
  const [err, setErr] = useState(false);
  if (err) return <div className="aspect-video grid place-items-center rounded-lg border border-[#E2E8F0] bg-[#F1F5F9] text-xs text-[#94A3B8]">Photo unavailable</div>;
  return <img src={`${API}${src}`} alt={alt} onError={() => setErr(true)}
    className="aspect-video w-full object-cover rounded-lg border border-[#E2E8F0]" />;
}

export default function PublicMaintenanceShare() {
  const { slug } = useParams();
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);

  useEffect(() => {
    document.title = "Shared Maintenance Issue";
    axios.get(`${API}/maintenance/${slug}`)
      .then(({ data }) => { setData(data); setState("ok"); })
      .catch(() => setState("notfound"));
  }, [slug]);

  if (state === "loading") return <div className="min-h-screen bg-[#EEF2F6] p-6"><div className="max-w-2xl mx-auto space-y-4"><div className="h-10 w-48 bg-[#E2E8F0] rounded animate-pulse" /><div className="h-64 bg-[#E2E8F0] rounded-2xl animate-pulse" /></div></div>;
  if (state === "notfound") return (
    <div className="min-h-screen grid place-items-center bg-[#EEF2F6] px-6 text-center" data-testid="maintenance-notfound">
      <div><h1 className="font-heading text-2xl font-bold text-navy">This link is invalid or no longer available.</h1></div>
    </div>
  );

  const issue = data.issue;

  return (
    <div className="min-h-screen bg-[#EEF2F6]">
      <header className="border-b border-[#E2E8F0] bg-white/90 backdrop-blur-xl">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4">
          <Logo />
          <h1 className="mt-3 font-heading text-2xl font-bold text-navy">Shared Maintenance Issue</h1>
          {data.property_address && <p className="text-sm text-[#475569] flex items-center gap-1.5"><MapPin size={14} className="text-brand" /> {data.property_address}</p>}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6" data-testid="public-maintenance">
        <p className="mb-4 text-xs text-[#64748B] bg-white border border-[#E2E8F0] rounded-lg px-3 py-2">
          Anyone with this link can view and respond to this shared TenantSeal maintenance issue.
        </p>

        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-4" data-testid="maintenance-issue-card">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 font-heading font-semibold text-navy"><Wrench size={16} className="text-brand" /> {issue.title}</span>
            {issue.severity && <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border ${SEV[issue.severity] || SEV.moderate}`}>{issue.severity}</span>}
          </div>
          {issue.description && <p className="mt-2 text-sm text-[#334155]">{issue.description}</p>}
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-[#94A3B8]">
            {issue.created_at && <span>Logged {fmt(issue.created_at)}</span>}
            {issue.reported_to_landlord_at && <span className="text-[#177245]">Reported {fmt(issue.reported_to_landlord_at)}</span>}
            {issue.resolved_at && <span className="text-[#177245]">Resolved {fmt(issue.resolved_at)}</span>}
          </div>
          {(issue.photos || []).length > 0 && (
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {issue.photos.map((ph) => <Img key={ph.image_path} src={ph.image_path} alt={issue.title} />)}
            </div>
          )}
          <RecipientResponse slug={slug} endpoint={`/maintenance/${slug}/event`}
            ackEvent="item_acknowledged" noteEvent="noted"
            ackLabel="Acknowledge" noteLabel="Add note" testId="maintenance-response" />
        </div>

        <footer className="mt-10 py-6 text-center">
          <a href="/" className="text-sm text-[#94A3B8] hover:text-brand">Prepared with TenantSeal</a>
        </footer>
      </main>
    </div>
  );
}
